/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { createFeature, createReducer, on } from '@ngrx/store';
import { AudioDevicesRoles } from 'web-audio-module';
import {
    alertToDevice,
    alertToHeadsets,
    ccHubAttachedEvent,
    ccHubEffectsInitialized,
    ccHubHeadsetConnectedEvent,
    ccHubInitialized,
    ccHubSerialNumber,
    deviceStateUpdate, fetchVersionSuccess,
    irrToDevice,
    irrToHeadsets,
    muteAction,
    muteOnAnswerUpdate,
    overrideCcHubHeadsetRequirement,
    rpiDebugToggle,
    setAuxAgentStatus,
    setFirmwareUpgrading,
    setFirmwareVersion,
    setPeripheralVolume
} from './cchub.actions';
import { AudioDevicesRolesInternal } from 'web-audio-module/dist/e-audio-devices-roles.enum';
import { toastExpired } from '../../notification/+state/notification.actions';
import { AuxliaryOutputsNames, CCHubButtons, DeviceData } from 'web-usb-module';
import { InputDevice, OutputDevice } from '../../usb/shared/model/device';
import { AgentStatus } from 'CalltakingCoreApi';
import { AuxRelay } from '../../core/model/aux-relay';

export type AuxiliaryOutput = AuxRelay;

export interface CChubState {
    effectsInitialized: boolean;
    ccHubAttached: boolean;
    ccHubInitialized: boolean;
    firmwareVersion: string;
    firmwareUpgrading: boolean;
    ccHubSn: string | undefined;
    headsetConnected: boolean;
    headsetDisconnectedAndTimedOut: boolean;
    muteStatus: { [peripheral: string]: { muted: boolean; muteOnAnswer: boolean } };
    volumeStatus: { [peripheral: string]: { volume: number } };
    ccHubHeadsetOverride: boolean;
    alertToHeadsets: boolean;
    alertToDevice: boolean;
    irrToHeadsets: boolean;
    irrToDevice: boolean;
    auxiliaryAgentStatusMap: {
        [key in AuxiliaryOutput]: AgentStatus | 'HEADSET_DISCONNECTED'
    };
    deviceState: DeviceData;
    inputs: InputDevice[];
    outputs: OutputDevice[];
    versionNumber: string;
}
const initialState: CChubState = {
    effectsInitialized: false,
    ccHubAttached: false,
    ccHubInitialized: false,
    firmwareVersion: undefined,
    firmwareUpgrading: false,
    ccHubSn: undefined,
    headsetConnected: false,
    headsetDisconnectedAndTimedOut: false,
    versionNumber: '',
    muteStatus: {
        [AudioDevicesRoles.HEADSET_1_MIC]: { muted: false, muteOnAnswer: false },
        [AudioDevicesRoles.HEADSET_2_MIC]: { muted: false, muteOnAnswer: false },
        [AudioDevicesRolesInternal.RPI_OUT]: { muted: false, muteOnAnswer: false }
    },
    volumeStatus: {
        [AudioDevicesRoles.HEADSET_1_MIC]: { volume: 5 },
        [AudioDevicesRoles.HEADSET_2_MIC]: { volume: 5 },
        [AudioDevicesRolesInternal.RPI_IN]: { volume: 5 },
        [AudioDevicesRoles.HEADSET_1_PHONE_SPEAKER]: { volume: 5 },
        [AudioDevicesRoles.HEADSET_2_PHONE_SPEAKER]: { volume: 5 },
        [AudioDevicesRolesInternal.RPI_OUT]: { volume: 5 }
    },
    ccHubHeadsetOverride: false,
    alertToHeadsets: true,
    alertToDevice: false,
    irrToHeadsets: true,
    irrToDevice: false,
    auxiliaryAgentStatusMap: {
        [AuxliaryOutputsNames.Auxiliary_output_1]: 'ON_CALL',
        [AuxliaryOutputsNames.Auxiliary_output_2]: 'READY',
        [AuxliaryOutputsNames.Auxiliary_output_3]: 'NOT_READY',
        [AuxliaryOutputsNames.Auxiliary_output_4]: 'HEADSET_DISCONNECTED'
    },
    deviceState: {
        DESKTOP_MIC_JS: { state: false, hidden: false },
        DESKTOP_MIC_PTT: { state: false, hidden: false },
        DESKTOP_MIC_MONITOR: { state: false, hidden: false },
        HEADSET1_JS: { state: false, hidden: false },
        HEADSET1_PTT: { state: false, hidden: false },
        HEADSET2_JS: { state: false, hidden: false },
        HEADSET2_PTT: { state: false, hidden: false },
        EPI_JS: { state: false, hidden: false },
        EPI_OFFHOOK: { state: false, hidden: false },
        FOOTSWITCH_PTT: { state: false, hidden: false },
        FOOTSWITCH_MONITOR: { state: false, hidden: false },
        EPE_PTT: { state: false, hidden: false }
    },
    inputs: [
        {
            id: AudioDevicesRoles.HEADSET_1_MIC,
            volumeId: AudioDevicesRoles.HEADSET_1_MIC,
            jackSenseId: CCHubButtons.HEADSET1_JS,
            name: 'Headset 1 Mic',
            abbreviation: 'HS1',
            direction: 'input',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: false,
            muted: false,
            muteOnAnswer: false
        },
        {
            id: AudioDevicesRoles.HEADSET_2_MIC,
            volumeId: AudioDevicesRoles.HEADSET_2_MIC,
            jackSenseId: CCHubButtons.HEADSET2_JS,
            name: 'Headset 2 Mic',
            abbreviation: 'HS2',
            direction: 'input',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: false,
            muted: false,
            muteOnAnswer: false
        },
        {
            id: AudioDevicesRolesInternal.RPI_IN,
            volumeId: AudioDevicesRolesInternal.RPI_IN,
            jackSenseId: CCHubButtons.EPE_PTT,
            name: 'RPI Mic',
            abbreviation: 'RDI',
            direction: 'input',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: false,
            muted: false,
            muteOnAnswer: false
        }
    ],
    outputs: [
        {
            id: AudioDevicesRoles.HEADSET_1_PHONE_SPEAKER,
            volumeId: AudioDevicesRoles.HEADSET_1_PHONE_SPEAKER,
            jackSenseId: CCHubButtons.HEADSET1_JS,
            name: 'Headset 1 Speaker',
            abbreviation: 'HS1',
            direction: 'output',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: false
        },
        {
            id: AudioDevicesRoles.HEADSET_2_PHONE_SPEAKER,
            volumeId: AudioDevicesRoles.HEADSET_2_PHONE_SPEAKER,
            jackSenseId: CCHubButtons.HEADSET2_JS,
            name: 'Headset 2 Speaker',
            abbreviation: 'HS2',
            direction: 'output',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: false
        },
        {
            id: AudioDevicesRolesInternal.RPI_OUT,
            volumeId: AudioDevicesRolesInternal.RPI_OUT,
            jackSenseId: CCHubButtons.EPE_PTT,
            name: 'RPI Speaker',
            abbreviation: 'RDI',
            direction: 'output',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: false
        },
        {
            id: AudioDevicesRolesInternal.LLR_3,
            volumeId: AudioDevicesRolesInternal.LLR_3,
            jackSenseId: undefined,
            name: AudioDevicesRolesInternal.LLR_3,
            abbreviation: AudioDevicesRolesInternal.LLR_3,
            direction: 'output',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: true
        },
        {
            id: AudioDevicesRolesInternal.LLR_1,
            volumeId: AudioDevicesRolesInternal.LLR_1,
            jackSenseId: undefined,
            name: AudioDevicesRolesInternal.LLR_1,
            abbreviation: AudioDevicesRolesInternal.LLR_1,
            direction: 'output',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: true
        },
        {
            id: AudioDevicesRolesInternal.LLR_2,
            volumeId: AudioDevicesRolesInternal.LLR_2,
            jackSenseId: undefined,
            name: AudioDevicesRolesInternal.LLR_2,
            abbreviation: AudioDevicesRolesInternal.LLR_2,
            direction: 'output',
            agc: false,
            volume: 0,
            boost: 0,
            threshold: 0,
            connected: true
        }
    ]
};
export const ccHubFeature = createFeature({
    name: 'cchub',
    reducer: createReducer(
        initialState,
        on(ccHubEffectsInitialized, (state): CChubState => {
            return { ...state, effectsInitialized: true };
        }),
        on(toastExpired, (state, { ref }): CChubState => {
            return ref === 'headsetTimeout' ? { ...state, headsetDisconnectedAndTimedOut: true } : { ...state };
        }),
        on(ccHubAttachedEvent, (state, { isAttached }): CChubState => ({ ...state, ccHubAttached: isAttached })),
        on(ccHubInitialized, (state) => ({ ...state, ccHubInitialized: true })),
        on(ccHubSerialNumber, (state, { sn }): CChubState => ({ ...state, ccHubSn: sn })),
        on(ccHubHeadsetConnectedEvent, (state, { isConnected }): CChubState => ({ ...state, headsetConnected: isConnected, headsetDisconnectedAndTimedOut: isConnected ? false : state.headsetDisconnectedAndTimedOut })),
        on(muteAction, (state, { peripheral, isMuted }): CChubState => {
            let muteStatus = { ...state.muteStatus };
            muteStatus[peripheral] = { ...muteStatus[peripheral], muted: isMuted !== undefined ? isMuted : !muteStatus[peripheral].muted };
            return { ...state, muteStatus: muteStatus };
        }),
        on(muteOnAnswerUpdate, (state, { peripheral, muteOnAnswer }): CChubState => {
            let muteStatus = { ...state.muteStatus };
            muteStatus[peripheral] = { ...muteStatus[peripheral], muteOnAnswer: muteOnAnswer };
            return { ...state, muteStatus: muteStatus };
        }),
        on(overrideCcHubHeadsetRequirement, (state, { required }): CChubState => ({ ...state, ccHubHeadsetOverride: required })),
        on(rpiDebugToggle, (state, { enabled }): CChubState => {
            const deviceState = {...state.deviceState, 'EPE_PTT': { state: enabled, hidden: false } };
            const inputs = state.inputs.map(input =>  input.id === 'RPI_IN' ? ({ ...input, connected: enabled }) : input );
            const outputs = state.outputs.map(output =>  output.id === 'RPI_OUT' ? ({ ...output, connected: enabled }) : output );
            return { ...state, inputs, outputs, deviceState };
        }),
        on(setPeripheralVolume, (state, { peripheral, volume }): CChubState => {
            let volumeStatus = { ...state.volumeStatus };
            volumeStatus[peripheral] = { volume: volume };
            return { ...state, volumeStatus: volumeStatus };
        }),
        on(alertToHeadsets, (state, { alert }): CChubState =>  ({ ...state, alertToHeadsets: alert })),
        on(alertToDevice, (state, { alert }): CChubState => ({ ...state, alertToDevice: alert })),
        on(irrToHeadsets, (state, { alert }): CChubState =>  ({ ...state, irrToHeadsets: alert })),
        on(irrToDevice, (state, { alert }): CChubState => ({ ...state, irrToDevice: alert })),
        on(setFirmwareVersion, (state, { value }): CChubState => ({ ...state, firmwareVersion: value})),
        on(setFirmwareUpgrading, (state, { value }): CChubState => ({ ...state, firmwareUpgrading: value})),
        on(setAuxAgentStatus, (state, { status, aux }): CChubState => {
            const newState = { ...state.auxiliaryAgentStatusMap };
            newState[aux] = status;
            return { ...state, auxiliaryAgentStatusMap: newState };
        }),
        on(deviceStateUpdate, (state, { deviceData }): CChubState => {
            // @ts-ignore
            const outputs = state.outputs.map(output => ({ ...output, connected: Boolean(!output.jackSenseId || deviceData[output.jackSenseId]?.state) }));
            // @ts-ignore
            const inputs = state.inputs.map(input => ({ ...input, connected: Boolean(!input.jackSenseId || deviceData[input.jackSenseId]?.state) }));
            return { ...state, deviceState: deviceData, inputs, outputs };
        }),
        on(fetchVersionSuccess, (state, { version }): CChubState => ({
            ...state, versionNumber: version
        }))
    )
});
